import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RadioList, Select } from '@treezor/design-system';

import { TKycSection } from '@/features/kyc/components/userInformation/InformationSection';
import { useUpdateUser } from '@/features/kyc/hooks/useUpdateUser';

import { BooleanRadio } from '@/features/user/components/CreateUser/BooleanRadio';
import { SanctionEmbargoQuestionaire } from '@/features/user/components/CreateUser/SanctionEmbargoQuestionaire';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { CustomColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { UserContentLabel } from '@/features/user/components/contentLayout';
import { IUser } from '@/features/user/store/user.model';
import { SanctionEmbargoSummary } from '@/features/user/components/CreateUser/SanctionEmbargoSummary';

interface IBusinessUserVerificationProps {
  user: IUser;
  isSituationOnEdition: boolean;
}

export function useBusinessUserVerificationProps({ user, isSituationOnEdition }: IBusinessUserVerificationProps) {
  const { t } = useTranslation();

  const {
    legalAnnualTurnOverOptions,
    legalNetIncomeRangeOptions,
    legalNumberOfEmployeeRangeOptions,
    sourceOfFundsOptions,
  } = useFieldOptions();

  const { onUpdateUser, handleValueChange, isLoading, watch, onCancelEdition } = useUpdateUser(user);

  const entitySanctionsQuestionnaireWatch = watch('entitySanctionsQuestionnaire');
  const legalAnnualTurnOverWatch = watch('legalAnnualTurnOver');
  const legalNetIncomeRangeWatch = watch('legalNetIncomeRange');
  const isOnStockExchangeWatch = watch('isOnStockExchange');
  const activityOutsideEuWatch = watch('activityOutsideEu');
  const economicSanctionsWatch = watch('economicSanctions');
  const residentCountriesSanctionsWatch = watch('residentCountriesSanctions');
  const involvedSanctionsWatch = watch('involvedSanctions');

  const situation = useMemo(
    () =>
      ({
        subTitle: t('pages.dashboard.createUser.form.blocks.situation'),
        ...(isSituationOnEdition
          ? {
              component: (
                <div className="flex flex-col gap-4 w-[600px]">
                  <div className="flex flex-col gap-3">
                    <CustomColumns columns={[1, 1]}>
                      <RadioList
                        label={t('pages.dashboard.createUser.form.legalAnnualTurnOver')}
                        options={legalAnnualTurnOverOptions}
                        value={legalAnnualTurnOverWatch}
                        onChange={(value: string) => handleValueChange(value, 'legalAnnualTurnOver')}
                        className="pt-2 gap-3"
                      />
                      <div className="flex flex-col gap-6">
                        <RadioList
                          label={t('pages.dashboard.createUser.form.legalNetIncomeRanges.title')}
                          options={legalNetIncomeRangeOptions}
                          value={legalNetIncomeRangeWatch}
                          onChange={(value: string) => handleValueChange(value, 'legalNetIncomeRange')}
                          className="pt-2 gap-3"
                        />
                        <BooleanRadio
                          label={t('pages.currentProfiles.profile.financial.isOnStockExchange')}
                          name="isOnStockExchange"
                          value={isOnStockExchangeWatch?.toString()}
                          handleRadioChange={(value: string) => handleValueChange(value, 'isOnStockExchange')}
                          testId="radio-kyc-is-on-stock-exchange"
                        />
                      </div>
                    </CustomColumns>
                    <CustomColumns columns={[1, 2]}>
                      <UserContentLabel>{t('pages.dashboard.createUser.form.sourceOfFunds.title')}</UserContentLabel>
                      <Select
                        options={sourceOfFundsOptions}
                        defaultValue={user?.sourceOfFunds}
                        noOptionsComponent={<CreateUserEmptyComponent />}
                        onChange={(value: string) => handleValueChange(value, 'sourceOfFunds')}
                        showDelete
                        size="s"
                        testId="select-kyc-entity-type"
                      />
                    </CustomColumns>
                    <CustomColumns columns={[1, 2]}>
                      <UserContentLabel>{t('pages.dashboard.createUser.form.legalNumberOfEmployee')}</UserContentLabel>
                      <Select
                        options={legalNumberOfEmployeeRangeOptions}
                        defaultValue={user?.legalNumberOfEmployeeRange}
                        noOptionsComponent={<CreateUserEmptyComponent />}
                        onChange={(value: string) => handleValueChange(value, 'legalNumberOfEmployeeRange')}
                        showDelete
                        size="s"
                        testId="select-kyc-legal-number-employee"
                      />
                    </CustomColumns>
                  </div>
                  <SanctionEmbargoQuestionaire
                    defaultValue={{
                      entitySanction: entitySanctionsQuestionnaireWatch,
                      activityOutsideEu: activityOutsideEuWatch?.toString(),
                      economicSanctions: economicSanctionsWatch?.toString(),
                      residentCountriesSanctions: residentCountriesSanctionsWatch?.toString(),
                      involvedSanctions: involvedSanctionsWatch?.toString(),
                    }}
                    handleValueChange={handleValueChange}
                    size="s"
                  />
                </div>
              ),
            }
          : {
              categories: [
                {
                  items: [
                    {
                      label: t('pages.dashboard.createUser.form.legalAnnualTurnOver'),
                      value: user?.legalAnnualTurnOver
                        ? t(`pages.dashboard.createUser.form.legalAnnualTurnOvers.${user.legalAnnualTurnOver}`)
                        : '/',
                    },
                    {
                      label: t('pages.dashboard.createUser.form.legalNetIncomeRanges.title'),
                      value: user?.legalNetIncomeRange
                        ? t(`pages.dashboard.createUser.form.legalNetIncomeRanges.${user.legalNetIncomeRange}`)
                        : '/',
                    },
                    {
                      label: t('pages.currentProfiles.profile.financial.isOnStockExchange'),
                      value: user?.isOnStockExchange
                        ? t(`translation:components.molecules.forms.${user.isOnStockExchange ? 'yes' : 'no'}`)
                        : '/',
                    },
                    {
                      label: t('pages.dashboard.createUser.form.sourceOfFunds.title'),
                      value: user?.sourceOfFunds
                        ? t(`pages.dashboard.createUser.form.sourceOfFunds.${user?.sourceOfFunds}`)
                        : '/',
                    },
                    {
                      label: t('pages.dashboard.createUser.form.legalNumberOfEmployee'),
                      value: user?.legalNumberOfEmployeeRange || '/',
                    },
                  ],
                },
                {
                  component: (
                    <SanctionEmbargoSummary
                      entitySanctionsQuestionnaire={user?.entitySanctionsQuestionnaire}
                      activityOutsideEu={user?.activityOutsideEu}
                      economicSanctions={user?.economicSanctions}
                      residentCountriesSanctions={user?.residentCountriesSanctions}
                      involvedSanctions={user?.involvedSanctions}
                    />
                  ),
                },
              ],
            }),
      }) as TKycSection,
    [
      activityOutsideEuWatch,
      economicSanctionsWatch,
      entitySanctionsQuestionnaireWatch,
      handleValueChange,
      involvedSanctionsWatch,
      isSituationOnEdition,
      legalAnnualTurnOverOptions,
      legalNetIncomeRangeOptions,
      legalNumberOfEmployeeRangeOptions,
      residentCountriesSanctionsWatch,
      sourceOfFundsOptions,
      t,
      user?.activityOutsideEu,
      user?.economicSanctions,
      user?.entitySanctionsQuestionnaire,
      user?.involvedSanctions,
      user.isOnStockExchange,
      user.legalAnnualTurnOver,
      user.legalNetIncomeRange,
      user?.legalNumberOfEmployeeRange,
      user?.residentCountriesSanctions,
      user?.sourceOfFunds,
      isOnStockExchangeWatch,
      legalAnnualTurnOverWatch,
      legalNetIncomeRangeWatch,
    ],
  );

  return { situation, isLoading, onUpdateUser, onCancelEdition };
}
